import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useSettings, logoUrl } from "../context/SettingsContext";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import GenderIcon from "../components/GenderIcon";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/roles";
import { CalendarDays, Loader2, Printer } from "lucide-react";

function currentMonth() { return new Date().toISOString().slice(0, 7); }

export default function Monthly() {
  const { t, dir } = useLang();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editable = canEdit(user);
  const [month, setMonth] = useState(currentMonth());
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/groups").then((r) => setGroups(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = group === "all" ? { month } : { month, group_id: group };
      const r = await api.get("/attendance/monthly", { params });
      setData(r.data);
    } catch (err) { console.error("Failed to load monthly", err); } finally { setLoading(false); }
  }, [month, group]);

  useEffect(() => { load(); }, [load]);

  const cycle = async (student, dateStr) => {
    const cur = student.records[dateStr];
    const next = cur === "present" ? "absent" : cur === "absent" ? "leave" : "present";
    try {
      await api.post("/attendance/mark", { student_id: student.id, date: dateStr, status: next });
      setData((prev) => {
        if (!prev) return prev;
        const students = prev.students.map((s) => {
          if (s.id !== student.id) return s;
          const records = { ...s.records, [dateStr]: next };
          const present = Object.values(records).filter((v) => v === "present").length;
          const absent = Object.values(records).filter((v) => v === "absent").length;
          const leave = Object.values(records).filter((v) => v === "leave").length;
          const total = present + absent;
          const percentage = total ? Math.round((present / total) * 1000) / 10 : 0;
          return { ...s, records, present, absent, leave, percentage };
        });
        return { ...prev, students };
      });
    } catch { toast.error("Error"); }
  };

  const dayNum = (d) => parseInt(d.slice(8, 10), 10);
  const groupLabel = group === "all" ? t("monthly.allGroups") : (groups.find((g) => g.id === group)?.name || "");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground">{t("monthly.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("monthly.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t("monthly.month")}</label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="month-input" className="w-[170px]" dir="ltr" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t("monthly.group")}</label>
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger className="w-[190px]" data-testid="monthly-group-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("monthly.allGroups")}</SelectItem>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button onClick={() => window.print()} data-testid="monthly-print-btn"
            className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90">
            <Printer className="h-4 w-4" /> {t("monthly.print")}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-emerald-500" /> {t("monthly.present")}</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-red-500" /> {t("monthly.absent")}</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-amber-500" /> {t("monthly.leave")}</span>
        {editable && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {t("monthly.legend")}</span>}
      </div>

      <div className="surface-card overflow-hidden rounded-xl border border-border bg-card">
        {loading && <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</div>}
        {!loading && data && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="monthly-table">
              <thead>
                <tr className="border-b border-border bg-secondary/60">
                  <th className="sticky start-0 z-10 bg-secondary/60 px-3 py-2 text-start font-medium text-muted-foreground min-w-[160px]">{t("monthly.student")}</th>
                  {data.days.map((d) => (
                    <th key={d} className="px-1 py-2 text-center font-medium text-muted-foreground w-8" dir="ltr">{dayNum(d)}</th>
                  ))}
                  <th className="px-2 py-2 text-center font-medium text-emerald-600">{t("monthly.present")}</th>
                  <th className="px-2 py-2 text-center font-medium text-red-600">{t("monthly.absent")}</th>
                  <th className="px-2 py-2 text-center font-medium text-amber-600">{t("monthly.leave")}</th>
                  <th className="px-2 py-2 text-center font-medium text-muted-foreground">{t("monthly.percentage")}</th>
                </tr>
              </thead>
              <tbody>
                {data.students.length === 0 && (
                  <tr><td colSpan={data.days.length + 5} className="px-4 py-10 text-center text-muted-foreground">{t("monthly.noData")}</td></tr>
                )}
                {data.students.map((s) => (
                  <tr key={s.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid={`monthly-row-${s.id}`}>
                    <td className="sticky start-0 z-10 bg-card px-3 py-2">
                      <div className="flex items-center gap-2">
                        <GenderIcon gender={s.gender} className="h-3.5 w-3.5" />
                        <button onClick={() => navigate(`/student/${s.id}`)} className="font-medium text-foreground hover:brand-text hover:underline text-start" data-testid={`monthly-name-${s.id}`}>
                          {s.name}
                        </button>
                      </div>
                    </td>
                    {data.days.map((d) => {
                      const st = s.records[d];
                      const cls = st === "present" ? "bg-emerald-500 text-white" : st === "absent" ? "bg-red-500 text-white" : st === "leave" ? "bg-amber-500 text-white" : "text-muted-foreground hover:bg-secondary";
                      return (
                        <td key={d} className="p-0.5 text-center">
                          <button onClick={() => editable && cycle(s, d)} disabled={!editable} data-testid={`cell-${s.id}-${d}`}
                            className={`flex h-7 w-7 items-center justify-center rounded text-xs font-semibold transition-colors ${cls}`}
                            title={`${d}`}>
                            {st === "present" ? "✓" : st === "absent" ? "✗" : st === "leave" ? "L" : "·"}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 text-center font-semibold text-emerald-600">{s.present}</td>
                    <td className="px-2 py-2 text-center font-semibold text-red-600">{s.absent}</td>
                    <td className="px-2 py-2 text-center font-semibold text-amber-600">{s.leave}</td>
                    <td className="px-2 py-2 text-center font-semibold brand-text">{s.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data && (
        <div id="print-report" data-testid="monthly-print-report">
          <style>{`@media print { @page { size: A4 landscape; margin: 10mm; } }`}</style>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "2px solid #111", paddingBottom: 12, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {logoUrl(settings.logo_path) ? <img src={logoUrl(settings.logo_path)} alt="logo" style={{ height: 48, width: 48, objectFit: "cover", borderRadius: 8 }} /> : null}
              <div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{settings.school_name}</div>
                <div style={{ fontSize: 12, color: "#444" }}>{t("monthly.reportTitle")} — {groupLabel} — {month}</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#444" }}>{t("detail.generated")}: {new Date().toISOString().slice(0, 10)}</div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
            <thead>
              <tr>
                <th style={{ border: "1px solid #999", padding: 3, textAlign: dir === "rtl" ? "right" : "left", background: "#f0f0f0" }}>{t("monthly.student")}</th>
                {data.days.map((d) => <th key={d} style={{ border: "1px solid #999", padding: 2, textAlign: "center", background: "#f0f0f0" }}>{dayNum(d)}</th>)}
                <th style={{ border: "1px solid #999", padding: 3, background: "#f0f0f0" }}>OK</th>
                <th style={{ border: "1px solid #999", padding: 3, background: "#f0f0f0" }}>NO</th>
                <th style={{ border: "1px solid #999", padding: 3, background: "#f0f0f0" }}>M</th>
                <th style={{ border: "1px solid #999", padding: 3, background: "#f0f0f0" }}>%</th>
              </tr>
            </thead>
            <tbody>
              {data.students.map((s) => (
                <tr key={s.id}>
                  <td style={{ border: "1px solid #999", padding: 3, whiteSpace: "nowrap" }}>{s.name}</td>
                  {data.days.map((d) => {
                    const st = s.records[d];
                    return <td key={d} style={{ border: "1px solid #999", padding: 2, textAlign: "center", fontWeight: 700 }}>{st === "present" ? "OK" : st === "absent" ? "NO" : st === "leave" ? "M" : ""}</td>;
                  })}
                  <td style={{ border: "1px solid #999", padding: 3, textAlign: "center" }}>{s.present}</td>
                  <td style={{ border: "1px solid #999", padding: 3, textAlign: "center" }}>{s.absent}</td>
                  <td style={{ border: "1px solid #999", padding: 3, textAlign: "center" }}>{s.leave}</td>
                  <td style={{ border: "1px solid #999", padding: 3, textAlign: "center" }}>{s.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8, fontSize: 10, color: "#444" }}>OK = {t("monthly.present")} · NO = {t("monthly.absent")} · M = {t("monthly.leave")}</div>
        </div>
      )}
    </div>
  );
}
